import {
  BloomReceiver,
  FetchNetworkAdapter,
  FlowerLoadError,
  FlowerReceiver,
  LocalStorageAdapter,
  createFlowerPlayer,
  createPlayerAdapter,
  loadFlowerSdk,
  resolveDeviceId,
  type ChannelReceiver,
  type FlowerFallbackReason,
  type PlayerDebugSnapshot,
} from '@apm/bloom-receiver-sdk';
import { config } from './config.js';
import { applyAudioMuted } from './audio-muted.js';
import { playbackBuffers } from './playback-buffers.js';
import { createAdAdapter } from './ads/create-ad-adapter.js';
import { adsEnabled } from './ads/ads-enabled.js';
import { offerAdapter } from './offers/create-offer-adapter.js';
import type { ReceiverEngine } from './receiver-engine.js';
import { serialTurns } from './serial-turns.js';

/**
 * v36 — **리시버를 만드는 유일한 자리** (절대 규칙 93 · `docs/72` §5.1).
 *
 * 엔진마다 플레이어가 다르므로 플레이어의 생성·디버그 스냅샷·정리까지 핸들이 쥔다. 앱(`useReceiver`)은
 * `ChannelReceiver` 와 이 핸들만 안다 — `BloomReceiver` 를 import 하는 앱 파일은 여기 하나다.
 *
 * ★ **BLOOM 은 동기다**(설계 §5.1 은 `Promise` 로 그렸다). BLOOM 엔진을 비동기로 만들면 `useReceiver` 가 리시버를
 * 한 틱 늦게 쥐어, 그 사이의 게이트 클릭·`autoStart` 가 비어 있는 ref 를 만난다 — FR7 은 동작 변화 0 이어야 했다.
 * **Flower 는 비동기다**(`createFlowerReceiver`) — 번들 적재와 `attachMedia` 를 기다려야 한다.
 */
export interface ReceiverHandle {
  receiver: ChannelReceiver;
  engine: ReceiverEngine;
  /** 디버그 패널이 읽는 플레이어 상태(지금의 `playerRef.current?.debugSnapshot()` 자리) */
  playerSnapshot(): PlayerDebugSnapshot | null;
  /** 엔진만의 진단 — Flower 면 조립된 재생목록·열리지 않은 브레이크(`docs/72` §5.5), BLOOM 이면 `null` */
  engineSnapshot(): unknown;
  /** `receiver.stop()` + 플레이어 정리 (규칙 27) */
  destroy(): void;
}

export interface CreateReceiverOptions {
  /** 어댑터가 `<video>` 쌍을 붙일 자리 */
  container: HTMLElement;
  channelId: string;
  /** BLOOM 세션 토큰 — 있으면 계정 축 이어보기 */
  authToken: string | null;
}

export function createReceiver(engine: 'bloom', opts: CreateReceiverOptions): ReceiverHandle {
  if (engine !== 'bloom')
    throw new Error(`createReceiver: 엔진 '${String(engine)}' 은 createFlowerReceiver 로`);
  return createBloomReceiver(opts);
}

/**
 * v36 — **Flower 번들**(`public/vendor/flower/README.md`). 바꿀 때는 그 디렉터리의 파일·`.sha256`·README 를 함께 바꾼다.
 * `BASE_URL` 을 앞에 붙인다 — 앱이 하위 경로에 설 수도 있다.
 */
export const FLOWER_BUNDLE = `${import.meta.env.BASE_URL}vendor/flower/flower-sdk-2.3.16-bloom.30-ea6cdd9.js`;

/** 폴백의 이유 — 번들을 못 넣었거나, Flower 가 첫 프레임 전에 물러났다(`docs/72` §2.2) */
export type EngineFallbackReason = 'bundle-load-failed' | FlowerFallbackReason;

/**
 * v36 — **Flower 엔진을 세운다** (절대 규칙 93~97). 순서는 번들 적재(페이지당 한 번 · 이때부터 페이지의 XHR 이
 * Flower 의 것이다) → 플레이어(`attachMedia` 먼저) → 리시버. 번들·플레이어가 실패하면 거절하고 — 부르는 쪽이
 * `bundle-load-failed` 로 폴백한다. 첫 프레임 전에 Flower 가 물러나면(라이브 창 · 시작 실패) `onFallback` 이 불린다.
 *
 * 기기 id·최소 간격의 저장소는 BLOOM 과 같은 `LocalStorageAdapter`(`fast.deviceId`·`fast.lastAdBreakEndedAt`)이고,
 * 광고 On/Off 는 요청마다 `adsEnabled()` 를, 상품 카드는 BLOOM 과 같은 `offerAdapter()`(토글이 그 안에 있다)를 쓴다.
 *
 * ★ **한 줄로 선다**(2026-10-20 · `serial-turns.ts`). 생성 도중에 부른 쪽이 물러나면(`signal` · 채널 전환 ·
 * `authToken` 변경) 그 생성은 **`<video>` 에 손대기 전에** 멈추고, 이미 붙었으면 스스로 정리한 뒤 거절한다(`FlowerCreationAborted`
 * — 부른 쪽은 이미 떠났으니 폴백하지 않는다). 다음 생성은 그 뒤에야 시작한다 — 전에는 둘이 같은 `<video>` 에
 * hls.js 를 하나씩 붙였다.
 */
export function createFlowerReceiver(
  opts: CreateReceiverOptions & {
    onFallback: (reason: EngineFallbackReason, cause: unknown) => void;
    signal?: AbortSignal;
  },
): Promise<ReceiverHandle> {
  return flowerTurn(() => buildFlowerReceiver(opts));
}

/** Flower 생성의 줄 — 쌍 `<video>` 가 페이지에 하나이므로 줄도 페이지에 하나다 */
const flowerTurn = serialTurns();

/** 부른 쪽이 물러나 멈춘 Flower 생성. 폴백의 이유가 아니다 */
export class FlowerCreationAborted extends Error {
  constructor() {
    super('Flower 생성을 부른 쪽이 물러났다');
    this.name = 'FlowerCreationAborted';
  }
}

async function buildFlowerReceiver(
  opts: CreateReceiverOptions & {
    onFallback: (reason: EngineFallbackReason, cause: unknown) => void;
    signal?: AbortSignal;
  },
): Promise<ReceiverHandle> {
  if (opts.signal?.aborted) throw new FlowerCreationAborted();
  const storage = new LocalStorageAdapter();
  const deviceId = resolveDeviceId('', storage);
  const { FlowerChannel } = await loadFlowerSdk({ src: FLOWER_BUNDLE, deviceId });
  // 번들을 기다리는 동안 물러났으면 `<video>` 에 손대지 않는다.
  if (opts.signal?.aborted) throw new FlowerCreationAborted();
  let player;
  try {
    player = await createFlowerPlayer({ container: opts.container, buffers: playbackBuffers() });
  } catch (e) {
    throw new FlowerLoadError('Flower 플레이어(hls.js)를 세우지 못했다', e);
  }
  // 붙는 동안 물러났으면 다음 생성이 줄에서 기다리고 있다 — 넘기기 전에 여기서 정리한다.
  if (opts.signal?.aborted) {
    player.destroy();
    throw new FlowerCreationAborted();
  }
  applyAudioMuted();
  const receiver = new FlowerReceiver({
    FlowerChannel,
    serverUrl: config.serverUrl,
    channelId: opts.channelId,
    hls: player.hls,
    media: player.media,
    storage,
    authToken: opts.authToken,
    adsEnabled,
    offers: offerAdapter(),
    onFallback: opts.onFallback,
  });
  const ready = player;
  return {
    receiver,
    engine: 'flower',
    // 더블 버퍼의 표지(앞 버퍼·꼬리 무장)는 흉내 내지 않는다(§5.5) — 엔진의 진단은 `engineSnapshot()` 이 낸다.
    playerSnapshot: () => null,
    engineSnapshot: () => receiver.flowerSnapshot(),
    destroy: () => {
      receiver.stop();
      ready.destroy();
    },
  };
}

function createBloomReceiver({
  container,
  channelId,
  authToken,
}: CreateReceiverOptions): ReceiverHandle {
  // **v12 — 어댑터를 기능 검사로 고른다** (`createPlayerAdapter`). MSE 가 있으면 지금까지의
  // hls.js 경로 그대로이고, 없으면(iPhone) 네이티브 HLS 다. 화면·상태·이벤트는 같다.
  /*
   * **`<video>` 한 쌍을 앱이 들고 있다가 넘긴다** (v19). 채널이 바뀌어도 같은 엘리먼트를
   * 쓰므로 WebKit 의 재생 허가가 남고, 전환이 **제스처 밖에서도** 시작된다 —
   * 그것이 iPhone 의 전환 게이트를 없앤 근거다 (`playback-buffers.ts`).
   */
  const player = createPlayerAdapter({ container, buffers: playbackBuffers() });
  /*
   * ★ **저장된 오디오 토글을 여기서 되건다** (v22). 어댑터가 새로 설 때마다 부르므로
   * **첫 진입과 채널 전환이 함께 덮인다** — `localStorage` 로 음소거가 켜진 채 문서가
   * 열리는 경우가 그 첫째다.
   *
   * 어댑터는 `volume` 을 읽지도 쓰지도 않으므로(그쪽 축은 `muted` 다) 순서가 상관없다.
   * 근거 전량은 `audio-muted.ts` 머리에 있다.
   */
  applyAudioMuted();
  const receiver = new BloomReceiver({
    serverUrl: config.serverUrl,
    // **v9 에서 설정값이 아니라 인자가 됐다.** 앱이 트는 채널이 하나뿐이 아니고,
    // 어느 것을 트는지는 「지금 볼 채널」 판정이 정한다 (`channel-selection.ts`).
    channelId,
    // 저장소에 값이 있으면 그것을 쓴다. 빈 문자열이면 SDK 가 생성한다.
    deviceId: '',
    player,
    network: new FetchNetworkAdapter({ authToken }),
    storage: new LocalStorageAdapter(),
    /*
     * v21. **언제나 주입한다.** 설정의 광고 On/Off 는 이 인자를 조건부로 주는 것이
     * 아니라 **어댑터 안에서** 갈린다 — 생성자 인자라 조건부로 주면 토글 한 번에
     * 리시버를 다시 세워야 하고, 그러면 **영상이 처음부터 다시 시작한다**
     * (절대 규칙 25·51 · `create-ad-adapter.ts`).
     */
    ads: createAdAdapter(),
    /*
     * v23. **언제나 주입한다.** 설정의 상품 추천 On/Off 는 이 인자를 조건부로 주는
     * 것이 아니라 **어댑터 안에서** 갈린다 — 생성자 인자라 조건부로 주면 토글 한 번에
     * 리시버를 다시 세워야 하고, 그러면 **영상이 처음부터 다시 시작한다**
     * (절대 규칙 25·60 · `create-offer-adapter.ts`).
     */
    offers: offerAdapter(),
  });
  return {
    receiver,
    engine: 'bloom',
    playerSnapshot: () => player.debugSnapshot(),
    engineSnapshot: () => null,
    destroy: () => {
      // 순서가 계약이다 — 리시버가 먼저 멈춰야 플레이어의 콜백이 죽은 리시버를 부르지 않는다.
      receiver.stop();
      player.destroy();
    },
  };
}
